import { useState } from 'react';
import { Alert, Button, Dialog, MoneyValue, SearchInput, lucideIcons, toast } from '@round/creatorbase-ui';
import useClientSelect from 'Modules/Plans/Clients/hooks/useClientSelect';
import { useCreatorbaseCurrencies } from '@round/creatorbase-helpers/api-hooks';
import CampaignItem from 'Modules/Plans/CreatorSearch/components/AddCreatorToCampaignsDropdown/CampaignItem/CampaignItem';
import ProjectCampaignsList from 'Modules/Plans/CreatorSearch/components/AddCreatorToCampaignsDropdown/ProjectCampaignsList/ProjectCampaignsList';
import {
    ProjectCampaignOption,
    useProjectCampaignGroups,
} from 'Modules/Plans/CreatorSearch/components/AddCreatorToCampaignsDropdown/useProjectCampaignGroups';
import {
    getDefaultCost,
    isCurrencyMismatch,
} from 'Modules/Plans/CreatorSearch/components/AddCreatorToCampaignsDropdown/CreatePostForm/helpers';
import { ResultCostsTable } from './components/ResultCostsTable/ResultCostsTable';
import { ResultCost, useAddResultsToCampaign } from './useAddResultsToCampaign';
import { OutreachResultRow } from '../../helpers';
import styles from './AddResultsToCampaignModal.module.css';

const { AlertTriangle } = lucideIcons;

const SEARCH_DEBOUNCE_MS = 500;

/** Results only ever come from TikTok, so only a TikTok campaign can take one. */
const PLATFORM = 'tiktok';

const TITLE = 'Add to campaign';
const SEARCH_PLACEHOLDER = 'Search projects...';
const SEARCH_LABEL = 'Search projects';
const PICK_DESCRIPTION = 'Pick the campaign these creators are to be posted in.';
const COST_DESCRIPTION = 'Set what each creator is to be paid, then add them all at once.';
const FIXED_COST_NOTE = 'This campaign costs its posts itself, so there is nothing to fill in.';
const CANCEL_LABEL = 'Cancel';

const pluralizeCreators = (count: number) => `${count} ${count === 1 ? 'creator' : 'creators'}`;

const getMissingCostsNote = (count: number) =>
    count === 1 ? 'One creator still needs a cost.' : `${count} creators still need a cost.`;

type Props = {
    results: OutreachResultRow[];
    getAvatarUrl: (userId: number) => string | undefined;
    areAvatarsLoading: boolean;
    onClose: () => void;
    /** Fired once a result has landed, so the selection behind it can be dropped. */
    onAdded: () => void;
};

export const AddResultsToCampaignModal = ({ results, getAvatarUrl, areAvatarsLoading, onClose, onAdded }: Props) => {
    const { selectedClient } = useClientSelect();
    const { getCurrencyById, getCurrencyOptionsById } = useCreatorbaseCurrencies();
    const { addResultsToCampaign, isAdding } = useAddResultsToCampaign();

    const [search, setSearch] = useState('');
    const [selectedCampaign, setSelectedCampaign] = useState<ProjectCampaignOption | null>(null);
    const [costs, setCosts] = useState<Record<number, MoneyValue>>({});
    // A row is only worth flagging once its input has been left, not the moment another row gets a value.
    const [touchedIds, setTouchedIds] = useState<Set<number>>(() => new Set());
    // Posted already, so a retry after a partial failure cannot cover them twice.
    const [postedIds, setPostedIds] = useState<Set<number>>(() => new Set());

    const { groups, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } = useProjectCampaignGroups(
        { client_id: selectedClient?.id, search, platform: PLATFORM, managed_by: 'creatorbase' },
        { enabled: Boolean(selectedClient?.id) }
    );

    // A cost belongs to the campaign's currency and fee rules, so it's cleared when the campaign changes.
    const [costsCampaignId, setCostsCampaignId] = useState(selectedCampaign?.id ?? null);
    if (costsCampaignId !== (selectedCampaign?.id ?? null)) {
        setCostsCampaignId(selectedCampaign?.id ?? null);
        setCosts({});
        setTouchedIds(new Set());
    }

    const areFeesEnabled = !!selectedClient?.is_payment_enabled;
    const isCostEditable = !!selectedCampaign?.is_post_cost_editable;
    const campaignCurrencyId = selectedCampaign?.currency_id;
    // Without fees a post is costed in the campaign's own currency.
    const defaultCurrency = getCurrencyOptionsById(campaignCurrencyId);

    const pendingResults = results.filter((result) => !postedIds.has(result.id));
    const pendingResultsById = new Map(pendingResults.map((result) => [result.id, result]));

    const getRate = (result: OutreachResultRow) => ({ cost: result.cost, currencyId: result.currency_id });

    const getCost = (resultId: number) => {
        const result = pendingResultsById.get(resultId);
        const defaultCost = result
            ? getDefaultCost(getRate(result), { areFeesEnabled, campaignCurrencyId, getCurrencyOptionsById })
            : { currency: defaultCurrency, value: '' };

        return costs[resultId] ?? defaultCost;
    };

    /** The endpoint refuses a costless post when the campaign leaves the cost to the client. */
    const hasCost = (resultId: number) => {
        if (!isCostEditable) {
            return true;
        }

        const cost = getCost(resultId);
        // Fees need the currency the cost was entered in, not just the amount.
        return !!cost.value && (!areFeesEnabled || !!cost.currency);
    };

    const resultsMissingCost = pendingResults.filter((result) => !hasCost(result.id));
    const missingCostIds = new Set(
        resultsMissingCost.filter((result) => touchedIds.has(result.id)).map((result) => result.id)
    );

    const mismatchedCurrencyIds = new Set(
        pendingResults
            .filter((result) =>
                isCurrencyMismatch(getRate(result), { isCostEditable, areFeesEnabled, campaignCurrencyId })
            )
            .map((result) => result.id)
    );

    const handleClose = () => {
        // Anything already posted has left the selection, half a success or not.
        if (postedIds.size) {
            onAdded();
        }

        onClose();
    };

    const handleAdd = async () => {
        if (!selectedCampaign) {
            return;
        }

        const entries: ResultCost[] = pendingResults.map((result) => ({
            result,
            cost: isCostEditable ? getCost(result.id) : null,
        }));

        const { addedResultIds, failedCount, errorMessage } = await addResultsToCampaign(selectedCampaign, entries);

        if (addedResultIds.length) {
            toast.success(`Added ${pluralizeCreators(addedResultIds.length)} to ${selectedCampaign.label}`);
        }

        if (!failedCount) {
            onAdded();
            onClose();
            return;
        }

        // One refusal is worth quoting; several are not one message.
        toast.error(
            failedCount === 1 && errorMessage ? errorMessage : `Could not add ${pluralizeCreators(failedCount)}`
        );

        setPostedIds((previous) => new Set([...previous, ...addedResultIds]));
    };

    return (
        <Dialog
            open
            onOpenChange={(open) => {
                // A batch in flight owns the modal until it settles.
                if (!open && !isAdding) {
                    handleClose();
                }
            }}
        >
            <Dialog.Content className={styles.content}>
                <Dialog.Header>
                    <Dialog.Title>{TITLE}</Dialog.Title>
                    <Dialog.Description>{selectedCampaign ? COST_DESCRIPTION : PICK_DESCRIPTION}</Dialog.Description>
                </Dialog.Header>

                <Dialog.Body className={styles.body}>
                    {selectedCampaign ? (
                        <>
                            <CampaignItem
                                isClearable
                                campaign={selectedCampaign}
                                onClear={() => setSelectedCampaign(null)}
                            />

                            {!isCostEditable && (
                                <Alert>
                                    <AlertTriangle />
                                    <Alert.Description>{FIXED_COST_NOTE}</Alert.Description>
                                </Alert>
                            )}

                            <ResultCostsTable
                                results={pendingResults}
                                campaignId={selectedCampaign.id}
                                campaignCurrency={getCurrencyById(selectedCampaign.currency_id)}
                                getCost={getCost}
                                onCostChange={(resultId, cost) =>
                                    setCosts((previous) => ({ ...previous, [resultId]: cost }))
                                }
                                onCostBlur={(resultId) => setTouchedIds((previous) => new Set(previous).add(resultId))}
                                missingCostIds={missingCostIds}
                                mismatchedCurrencyIds={mismatchedCurrencyIds}
                                isCostEditable={isCostEditable}
                                areFeesEnabled={areFeesEnabled}
                                isDisabled={isAdding}
                                getAvatarUrl={getAvatarUrl}
                                areAvatarsLoading={areAvatarsLoading}
                            />

                            {resultsMissingCost.length > 0 && (
                                <p className={styles.missingCosts} role="status">
                                    {getMissingCostsNote(resultsMissingCost.length)}
                                </p>
                            )}
                        </>
                    ) : (
                        <>
                            <SearchInput
                                placeholder={SEARCH_PLACEHOLDER}
                                aria-label={SEARCH_LABEL}
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                debounce={SEARCH_DEBOUNCE_MS}
                                disabled={!selectedClient?.id}
                            />

                            <ProjectCampaignsList
                                groups={groups}
                                isLoading={isLoading}
                                hasError={Boolean(error)}
                                hasNextPage={hasNextPage}
                                isFetchingNextPage={isFetchingNextPage}
                                onEndReached={() => {
                                    if (hasNextPage && !isFetchingNextPage) {
                                        fetchNextPage();
                                    }
                                }}
                                onSelect={setSelectedCampaign}
                                selectedCampaign={selectedCampaign}
                            />
                        </>
                    )}
                </Dialog.Body>

                <Dialog.Footer>
                    <Button variant="outline" size="sm" onClick={handleClose} disabled={isAdding}>
                        {CANCEL_LABEL}
                    </Button>
                    <Button
                        size="sm"
                        onClick={handleAdd}
                        disabled={
                            !selectedCampaign || !pendingResults.length || resultsMissingCost.length > 0 || isAdding
                        }
                        isLoading={isAdding}
                    >
                        Add {pluralizeCreators(pendingResults.length)}
                    </Button>
                </Dialog.Footer>
            </Dialog.Content>
        </Dialog>
    );
};
